import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

export default class extends Controller {
  static values = {
    defaultKiwisaver: Number,
    defaultAcc: Number
  }

  static targets = [
    "baseSalaryInput", "kiwisaverInput", "accInput",
    "annualLeaveWeeksInput", "sickLeaveDaysInput", "publicHolidaysDaysInput",
    "overheadTypeSelect", "overheadValueInput", "overheadUnit",
    "allowancesContainer", "allowanceRow",
    "advancedOptions", "advancedToggle",
    "resultsPanel", "loadingIndicator", "introContent",
    "totalCost", "onCostAmount", "onCostPercent",
    "breakdownBody",
    "annualTotal", "monthlyTotal", "fortnightlyTotal", "weeklyTotal",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._debounce.schedule(detectEventType(event), this.baseSalaryInputTarget.value)
  }

  optionChanged() {
    this._syncOverheadUnit()
    this._debounce.schedule("toggle", this.baseSalaryInputTarget.value)
  }

  toggleAdvanced(event) {
    event.preventDefault()
    const hidden = this.advancedOptionsTarget.classList.toggle("hidden")
    this.advancedToggleTarget.textContent = hidden ? "Show more options" : "Hide options"
  }

  addAllowance(event) {
    event.preventDefault()
    const rows = this.allowanceRowTargets
    const template = rows[0]
    if (!template) return
    const clone = template.cloneNode(true)
    clone.querySelectorAll("input").forEach((input) => { input.value = "" })
    this.allowancesContainerTarget.appendChild(clone)
  }

  removeAllowance(event) {
    event.preventDefault()
    const row = event.target.closest("[data-salary-lab-cost-to-employ-target='allowanceRow']")
    if (row && this.allowanceRowTargets.length > 1) {
      row.remove()
    } else if (row) {
      row.querySelectorAll("input").forEach((input) => { input.value = "" })
    }
    this._debounce.schedule("toggle", this.baseSalaryInputTarget.value)
  }

  submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _collectAllowances() {
    return this.allowanceRowTargets
      .map((row) => {
        const inputs = row.querySelectorAll("input")
        const label = inputs[0]?.value?.trim() || ""
        const amount = parseNumber(inputs[1]?.value) || 0
        return { label, amount }
      })
      .filter((a) => a.amount > 0)
  }

  _buildParams() {
    const baseSalary = parseNumber(this.baseSalaryInputTarget.value)
    const params = {
      type: "cost_to_employ",
      base_salary: baseSalary,
      kiwisaver_rate: this.hasKiwisaverInputTarget ? this.kiwisaverInputTarget.value : this.defaultKiwisaverValue,
      acc_levy_rate: this.hasAccInputTarget ? this.accInputTarget.value : this.defaultAccValue,
      overhead_type: this.hasOverheadTypeSelectTarget ? this.overheadTypeSelectTarget.value : "percentage",
      overhead_value: this.hasOverheadValueInputTarget ? parseNumber(this.overheadValueInputTarget.value) || 0 : 0,
      allowances: this._collectAllowances()
    }

    if (this.hasAnnualLeaveWeeksInputTarget) params.annual_leave_weeks = this.annualLeaveWeeksInputTarget.value
    if (this.hasSickLeaveDaysInputTarget) params.sick_leave_days = this.sickLeaveDaysInputTarget.value
    if (this.hasPublicHolidaysDaysInputTarget) params.public_holidays_days = this.publicHolidaysDaysInputTarget.value

    return params
  }

  async _performCalculation() {
    const baseSalary = parseNumber(this.baseSalaryInputTarget.value)
    if (!baseSalary || baseSalary <= 0) return

    this._showLoading()

    try {
      const params = this._buildParams()
      const data = await calculate(params)
      this._renderResults(data)
      this.element.dataset.currentParams = JSON.stringify(params)
      trackCalculatorUsed("cost_to_employ", { salaryAmount: baseSalary })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message || "Calculation failed. Please try again."
      }
    }
  }

  _renderResults(data) {
    if (this.hasTotalCostTarget) {
      this.totalCostTargets.forEach((el) => { el.textContent = formatCurrency(data.total_cost_to_employ) })
    }
    if (this.hasOnCostAmountTarget) this.onCostAmountTarget.textContent = formatCurrency(data.on_cost_amount)
    if (this.hasOnCostPercentTarget) this.onCostPercentTarget.textContent = `${data.on_cost_percentage}%`

    if (this.hasBreakdownBodyTarget) {
      this.breakdownBodyTarget.innerHTML = data.cost_breakdown.map((item) => {
        return `<tr class="border-t border-gray-100">
          <td class="py-2 text-sm text-gray-600">${item.label}</td>
          <td class="py-2 text-sm text-right tabular-nums font-medium text-gray-900">${formatCurrency(item.amount)}</td>
        </tr>`
      }).join("")
    }

    if (data.frequencies) {
      if (this.hasAnnualTotalTarget) this.annualTotalTarget.textContent = formatCurrency(data.frequencies.annual)
      if (this.hasMonthlyTotalTarget) this.monthlyTotalTarget.textContent = formatCurrency(data.frequencies.monthly)
      if (this.hasFortnightlyTotalTarget) this.fortnightlyTotalTarget.textContent = formatCurrency(data.frequencies.fortnightly)
      if (this.hasWeeklyTotalTarget) this.weeklyTotalTarget.textContent = formatCurrency(data.frequencies.weekly)
    }

    this._showResults()

    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent =
        `Total cost to employ: ${formatCurrency(data.total_cost_to_employ)} per year. ` +
        `On-costs: ${formatCurrency(data.on_cost_amount)} (${data.on_cost_percentage}%).`
    }
  }

  _syncOverheadUnit() {
    if (!this.hasOverheadUnitTarget || !this.hasOverheadTypeSelectTarget) return
    this.overheadUnitTarget.textContent = this.overheadTypeSelectTarget.value === "fixed" ? "$/year" : "%"
  }

  _showLoading() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.remove("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.add("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }

  _showResults() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add("hidden")
    if (this.hasResultsPanelTarget) this.resultsPanelTarget.classList.remove("hidden")
    if (this.hasIntroContentTarget) this.introContentTarget.classList.add("hidden")
  }
}
